import {
  POST_COMPOSE_CONTENT_TYPES,
  normalizePostComposeContentType,
} from '../config/postComposeConstants';
import { resolveThreadPostContentType } from './discussionUtils';

const PERMALINK_CONTENT_TYPES = new Set(POST_COMPOSE_CONTENT_TYPES);

export function slugifyDiscussionTitle(title) {
  return String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}

export function isInternalDraftSlug(slug) {
  return /^draft-[a-f0-9]{8,}$/i.test(String(slug || '').trim());
}

function discussionSlugBase(slug) {
  return String(slug || '')
    .trim()
    .toLowerCase()
    .replace(/-[a-f0-9]{8}$/i, '')
    .replace(/-\d+$/, '');
}

const PLACEHOLDER_DISCUSSION_SLUG_BASES = new Set([
  'post',
  'article',
  'status-update',
  'untitled',
  'untitled-draft',
]);

export function isPlaceholderDiscussionSlug(slug) {
  const s = String(slug || '').trim().toLowerCase();
  if (!s || isInternalDraftSlug(s)) return true;
  return PLACEHOLDER_DISCUSSION_SLUG_BASES.has(discussionSlugBase(s));
}

export function normalizePermalinkContentType(type) {
  const t = String(type || '').trim().toLowerCase();
  return PERMALINK_CONTENT_TYPES.has(t) ? t : '';
}

/** Parse `/posts/:contentType/:slug` or `/posts/p/:slug` paths. */
export function parseDiscussionPermalinkPath(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '');
  const typed = path.match(/^\/posts\/(story|question|article|announcement)\/([^/]+)$/i);
  if (typed) {
    return {
      contentType: typed[1].toLowerCase(),
      slug: decodeURIComponent(typed[2]),
      needsCanonical: false,
    };
  }
  const generic = path.match(/^\/posts\/p\/([^/]+)$/i);
  if (generic) {
    return {
      contentType: 'p',
      slug: decodeURIComponent(generic[1]),
      needsCanonical: true,
    };
  }
  return null;
}

export function isDiscussionPermalinkPath(pathname) {
  return Boolean(parseDiscussionPermalinkPath(pathname));
}

export function resolveThreadContentTypeForPermalink(thread) {
  if (thread && typeof thread === 'object') {
    return resolveThreadPostContentType(thread);
  }
  return 'article';
}

export function previewDiscussionPermalinkSlug({ title = '', body = '', contentType, savedDraftSlug = '' }) {
  const type = normalizePostComposeContentType(contentType);
  const titleSlug = slugifyDiscussionTitle(String(title || '').trim());
  if (titleSlug) return titleSlug;
  if (type === 'story') {
    const plain = String(body || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (plain.length >= 3) return slugifyDiscussionTitle(plain.slice(0, 80));
  }
  const saved = String(savedDraftSlug || '').trim();
  if (saved && !isInternalDraftSlug(saved) && !isPlaceholderDiscussionSlug(saved)) return saved;
  return '';
}

export function buildDiscussionPermalinkPath(threadOrSlug, contentType) {
  let slug = '';
  let type = normalizePermalinkContentType(contentType);

  if (threadOrSlug && typeof threadOrSlug === 'object') {
    slug = String(threadOrSlug.slug || '').trim();
    if (!type) type = resolveThreadContentTypeForPermalink(threadOrSlug);
  } else {
    slug = String(threadOrSlug || '').trim();
  }

  if (!slug || isInternalDraftSlug(slug) || isPlaceholderDiscussionSlug(slug)) return '';
  if (!type) type = 'article';
  return `/posts/${type}/${encodeURIComponent(slug)}`;
}

export function buildDiscussionPermalinkUrl(threadOrSlug, { origin, contentType } = {}) {
  const path = buildDiscussionPermalinkPath(threadOrSlug, contentType);
  if (!path) return '';
  const base =
    origin ||
    (typeof window !== 'undefined' ? window.location.origin : '') ||
    '';
  return `${base}${path}`;
}

export function buildGenericDiscussionPermalinkPath(slug) {
  const s = String(slug || '').trim();
  if (!s || isInternalDraftSlug(s) || isPlaceholderDiscussionSlug(s)) return '';
  return `/posts/p/${encodeURIComponent(s)}`;
}

/** Copy text with Clipboard API, falling back to execCommand for older/insecure contexts. */
export async function copyTextToClipboard(text) {
  const value = String(text || '');
  if (!value) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through to legacy copy
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.left = '-1000px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export function resolveShareableDiscussionPermalinkUrl(thread, slug, origin) {
  const fromThread = buildDiscussionPermalinkUrl(thread || (slug ? { slug } : null), { origin });
  if (fromThread) return fromThread;

  if (typeof window !== 'undefined') {
    const onPermalink = parseDiscussionPermalinkPath(window.location.pathname);
    if (onPermalink?.slug) {
      return `${window.location.origin}${window.location.pathname}`;
    }
  }

  return '';
}
